import { useEffect, useRef, useState } from 'react';
import { CheckCircle, Send, ChevronDown, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/1oy2wqev_ICON.png";

const roles = [
  'SVP of Payments',
  'Chief Technology Officer (CTO)',
  'CEO / President',
  'Chief Revenue Officer (CRO)',
  'Chief Financial Officer (CFO)',
  'Head of Digital Banking',
  'IT Director',
  'Investor / Advisor',
  'Other',
];

const initialForm = {
  full_name: '',
  institution: '',
  role: '',
  email: '',
  message: '',
};

export default function DemoSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Name is required';
    if (!form.institution.trim()) e.institution = 'Institution name is required';
    if (!form.role) e.role = 'Please select your role';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email address';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API}/demo-request`, form);
      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      setSubmitError('Something went wrong. Please email us directly at hello@nexgenpay.com');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="demo"
      data-testid="demo-section"
      ref={sectionRef}
      className="relative section-padding bg-black"
      style={{ scrollMarginTop: '80px' }}
    >
      {/* Green glow */}
      <div className="absolute inset-0 flex items-end justify-center pointer-events-none overflow-hidden">
        <div className="w-[900px] h-[400px] opacity-8"
          style={{ background: 'radial-gradient(ellipse, rgba(0,255,0,0.08), transparent 70%)' }} />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left: Copy */}
          <div
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(-40px)',
              transition: 'all 0.8s ease',
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="green-line" />
              <span className="text-[#00FF00] font-mono text-xs uppercase tracking-widest">Request a Demo</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-tight mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Ready to Reclaim
              <br />
              <span className="text-[#00FF00]">Your Revenue?</span>
            </h2>
            <p className="text-[#A3A3A3] text-base leading-relaxed mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Schedule a personalized demo with our team. We'll walk through how NexGenPay can transform your institution's digital payment infrastructure — from strategy to deployment.
            </p>

            {/* What to expect */}
            <div className="flex flex-col gap-4">
              {[
                { label: '30-min discovery call', sub: 'Understand your current infrastructure and goals' },
                { label: 'Custom ROI analysis', sub: 'Revenue projections modeled to your institution size' },
                { label: 'Live product walkthrough', sub: 'See the white-label eWallet in action' },
                { label: 'Pilot proposal', sub: 'Custom 6–9 month deployment roadmap' },
              ].map((item, i) => (
                <div key={item.label} className="flex items-start gap-3"
                  style={{
                    opacity: revealed ? 1 : 0,
                    transform: revealed ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.6s ease ${0.3 + i * 0.1}s`,
                  }}>
                  <div className="flex-shrink-0 mt-0.5">
                    <CheckCircle size={18} color="#00FF00" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {item.label}
                    </div>
                    <div className="text-[#A3A3A3] text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {item.sub}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Logo */}
            <div className="mt-12 pt-8 border-t border-[#1A1A1A] flex items-center gap-4">
              <img src={ICON_URL} alt="NX" className="h-12 w-12 object-contain" />
              <div>
                <div className="text-white font-bold text-sm uppercase tracking-widest"
                  style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                  NexGenPay eWallet
                </div>
                <div className="text-[#00FF00] text-xs font-mono uppercase tracking-widest">
                  Next Generation Payments
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            style={{
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 0.8s ease 0.2s',
            }}
          >
            {success ? (
              <div
                data-testid="demo-success-message"
                className="border border-[#00FF00] p-10 text-center"
                style={{ background: 'rgba(0,255,0,0.05)', boxShadow: '0 0 40px rgba(0,255,0,0.1)' }}
              >
                <div className="flex justify-center mb-6">
                  <div style={{ animation: 'glow-pulse 2s ease-in-out infinite' }}>
                    <CheckCircle size={56} color="#00FF00" />
                  </div>
                </div>
                <h3 className="text-white text-xl font-black uppercase mb-4"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Demo Request Received
                </h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Thank you! Our team will review your request and reach out within 24 business hours to schedule your personalized demo.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="demo-reset-btn"
                  className="btn-outline-neon text-xs"
                  style={{ fontFamily: 'Unbounded, sans-serif', padding: '12px 24px' }}
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="demo-form"
                className="border border-[#1A1A1A] p-8"
                style={{ background: '#0A0A0A' }}
                noValidate
              >
                <h3 className="text-white font-bold text-sm uppercase tracking-wide mb-6"
                  style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                  Schedule Your Demo
                </h3>

                <div className="flex flex-col gap-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-mono text-[#A3A3A3] uppercase tracking-widest mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      data-testid="demo-input-name"
                      placeholder="John Smith"
                      className="w-full bg-black border text-white text-sm px-4 py-3 focus:outline-none transition-colors duration-300"
                      style={{
                        borderColor: errors.full_name ? '#EF4444' : '#333',
                        fontFamily: 'Manrope, sans-serif',
                      }}
                      onFocus={(e) => !errors.full_name && (e.target.style.borderColor = '#00FF00')}
                      onBlur={(e) => !errors.full_name && (e.target.style.borderColor = '#333')}
                    />
                    {errors.full_name && (
                      <div className="flex items-center gap-1 mt-1 text-[#EF4444] text-xs">
                        <AlertCircle size={12} /> {errors.full_name}
                      </div>
                    )}
                  </div>

                  {/* Institution */}
                  <div>
                    <label className="block text-xs font-mono text-[#A3A3A3] uppercase tracking-widest mb-2">
                      Institution Name *
                    </label>
                    <input
                      type="text"
                      name="institution"
                      value={form.institution}
                      onChange={handleChange}
                      data-testid="demo-input-institution"
                      placeholder="First Community Bank"
                      className="w-full bg-black border text-white text-sm px-4 py-3 focus:outline-none transition-colors duration-300"
                      style={{
                        borderColor: errors.institution ? '#EF4444' : '#333',
                        fontFamily: 'Manrope, sans-serif',
                      }}
                      onFocus={(e) => !errors.institution && (e.target.style.borderColor = '#00FF00')}
                      onBlur={(e) => !errors.institution && (e.target.style.borderColor = '#333')}
                    />
                    {errors.institution && (
                      <div className="flex items-center gap-1 mt-1 text-[#EF4444] text-xs">
                        <AlertCircle size={12} /> {errors.institution}
                      </div>
                    )}
                  </div>

                  {/* Role */}
                  <div>
                    <label className="block text-xs font-mono text-[#A3A3A3] uppercase tracking-widest mb-2">
                      Your Role *
                    </label>
                    <div className="relative">
                      <select
                        name="role"
                        value={form.role}
                        onChange={handleChange}
                        data-testid="demo-select-role"
                        className="w-full bg-black border text-sm px-4 py-3 focus:outline-none appearance-none transition-colors duration-300 cursor-pointer"
                        style={{
                          borderColor: errors.role ? '#EF4444' : '#333',
                          fontFamily: 'Manrope, sans-serif',
                          color: form.role ? '#fff' : '#A3A3A3',
                        }}
                        onFocus={(e) => !errors.role && (e.target.style.borderColor = '#00FF00')}
                        onBlur={(e) => !errors.role && (e.target.style.borderColor = '#333')}
                      >
                        <option value="" disabled>Select your role</option>
                        {roles.map((r) => (
                          <option key={r} value={r} style={{ background: '#0A0A0A', color: '#fff' }}>{r}</option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] pointer-events-none" />
                    </div>
                    {errors.role && (
                      <div className="flex items-center gap-1 mt-1 text-[#EF4444] text-xs">
                        <AlertCircle size={12} /> {errors.role}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-mono text-[#A3A3A3] uppercase tracking-widest mb-2">
                      Work Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      data-testid="demo-input-email"
                      placeholder="john.smith@firstcommunitybank.com"
                      className="w-full bg-black border text-white text-sm px-4 py-3 focus:outline-none transition-colors duration-300"
                      style={{
                        borderColor: errors.email ? '#EF4444' : '#333',
                        fontFamily: 'Manrope, sans-serif',
                      }}
                      onFocus={(e) => !errors.email && (e.target.style.borderColor = '#00FF00')}
                      onBlur={(e) => !errors.email && (e.target.style.borderColor = '#333')}
                    />
                    {errors.email && (
                      <div className="flex items-center gap-1 mt-1 text-[#EF4444] text-xs">
                        <AlertCircle size={12} /> {errors.email}
                      </div>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-mono text-[#A3A3A3] uppercase tracking-widest mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      data-testid="demo-input-message"
                      placeholder="Tell us about your institution and specific challenges..."
                      rows={3}
                      className="w-full bg-black border border-[#333] text-white text-sm px-4 py-3 focus:outline-none focus:border-[#00FF00] transition-colors duration-300 resize-none"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                    />
                  </div>

                  {submitError && (
                    <div data-testid="demo-submit-error" className="flex items-center gap-2 text-[#EF4444] text-xs p-3 border border-[#EF4444] border-opacity-30 bg-red-500 bg-opacity-5">
                      <AlertCircle size={14} /> {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="demo-submit-btn"
                    disabled={submitting}
                    className="w-full btn-neon flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Unbounded, sans-serif', padding: '16px 32px' }}
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Request Your Demo <Send size={16} />
                      </>
                    )}
                  </button>

                  <p className="text-[#A3A3A3] text-xs text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    No spam. Response within 24 business hours.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
